import { useMutation, useQuery, useQueries } from '@tanstack/react-query';

import api from 'api';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import type { Agent } from 'types';

export const agentsKeys = {
    all: ['agents'] as const,
    byRole: (role: AgentRoles) => ['agents', 'role', role] as const,
};

export const useAgentsByRole = (role: AgentRoles, options?: { enabled?: boolean }) => {
    return useQuery<Agent[]>({
        queryKey: agentsKeys.byRole(role),
        queryFn: async () => {
            const response = await api.agents.get(role);
            return response.data ?? [];
        },
        enabled: options?.enabled ?? true,
        staleTime: 60 * 1000,
    });
};

/** Load sales / backoffice / consultant agent lists in parallel based on visibility. */
export const useAgentRoleLists = (options: {
    enabled?: boolean;
    includeSales: boolean;
    includeBackoffice: boolean;
    includeConsultant: boolean;
}) => {
    const { enabled = true, includeSales, includeBackoffice, includeConsultant } = options;

    const results = useQueries({
        queries: [
            {
                queryKey: agentsKeys.byRole(AgentRoles.SALES),
                queryFn: async () => {
                    const response = await api.agents.get(AgentRoles.SALES);
                    return (response.data ?? []) as Agent[];
                },
                enabled: enabled && includeSales,
                staleTime: 60 * 1000,
            },
            {
                queryKey: agentsKeys.byRole(AgentRoles.BACKOFFICE),
                queryFn: async () => {
                    const response = await api.agents.get(AgentRoles.BACKOFFICE);
                    return (response.data ?? []) as Agent[];
                },
                enabled: enabled && includeBackoffice,
                staleTime: 60 * 1000,
            },
            {
                queryKey: agentsKeys.byRole(AgentRoles.CONSULTANT),
                queryFn: async () => {
                    const response = await api.agents.get(AgentRoles.CONSULTANT);
                    return (response.data ?? []) as Agent[];
                },
                enabled: enabled && includeConsultant,
                staleTime: 60 * 1000,
            },
        ],
    });

    const [salesQuery, backofficeQuery, consultantQuery] = results;

    return {
        salesAgents: salesQuery.data ?? [],
        backofficeAgents: backofficeQuery.data ?? [],
        consultantAgents: consultantQuery.data ?? [],
        isLoading: results.some((q) => q.isLoading && q.fetchStatus !== 'idle'),
        isError: results.some((q) => q.isError),
        error: results.find((q) => q.isError)?.error,
    };
};

export interface AssignAgentVariables {
    role: AgentRoles;
    agentId: number;
    applicationIds: number[];
}

export const useAssignAgentMutation = () => {
    return useMutation({
        mutationFn: async ({ role, agentId, applicationIds }: AssignAgentVariables) => {
            await Promise.all(
                applicationIds.map((applicationId) =>
                    api.agents.assignAgent(role, {
                        agent_id: agentId,
                        application_id: applicationId,
                    })
                )
            );
        },
    });
};
